<template>
  <div class="px-2 py-2">
    <p class="px-2 mb-1 text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
      My Calendars
    </p>

    <div v-if="loading" class="space-y-1 animate-pulse px-2">
      <div v-for="i in 3" :key="i" class="h-6 bg-gray-100 dark:bg-gray-800 rounded"></div>
    </div>

    <template v-else>
      <template v-for="cal in calendars" :key="cal.id">
        <!-- Parent calendar row -->
        <div
          class="group w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <button class="flex items-center gap-2.5 flex-1 min-w-0 text-left" @click="emit('toggle', cal.id)">
            <CalendarCheckbox :checked="visibleIds.includes(cal.id)" :color="cal.color_code || '#3b82f6'" />
            <span class="text-sm text-gray-700 dark:text-gray-300 truncate flex-1 leading-none">
              {{ cal.identity }}
            </span>
          </button>
          <button
            class="flex-shrink-0 w-6 h-6 hidden group-hover:flex items-center justify-center rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            :title="copiedId === cal.id ? 'Copied' : 'Copy'"
            @click.stop="copyText(cal.id, cal.identity)"
          >
            <svg v-if="copiedId === cal.id" class="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
            <svg v-else class="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
            </svg>
          </button>
        </div>

        <!-- Sub-calendar rows -->
        <template v-if="cal.sub_calendars && cal.sub_calendars.length">
          <div
            v-for="sub in cal.sub_calendars"
            :key="sub.id"
            class="group w-full flex items-center gap-2.5 pl-6 pr-2 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <button class="flex items-center gap-2.5 flex-1 min-w-0 text-left" @click="emit('toggle', sub.id)">
              <CalendarCheckbox :checked="visibleIds.includes(sub.id)" :color="sub.color_code || '#3b82f6'" size="sm" />
              <span class="text-xs text-gray-600 dark:text-gray-400 truncate flex-1 leading-none">
                {{ sub.display_name || sub.identity }}
              </span>
            </button>
            <button
              class="flex-shrink-0 w-6 h-6 hidden group-hover:flex items-center justify-center rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              :title="copiedId === sub.id ? 'Copied' : 'Copy'"
              @click.stop="copyText(sub.id, sub.display_name || sub.identity)"
            >
              <svg v-if="copiedId === sub.id" class="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
              <svg v-else class="w-3 h-3 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
              </svg>
            </button>
          </div>
        </template>
      </template>

      <p v-if="!loading && calendars.length === 0" class="px-2 text-xs text-gray-400 dark:text-gray-500">
        No calendars connected.
      </p>
    </template>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import CalendarCheckbox from './CalendarCheckbox.vue'

defineProps({
  calendars:  { type: Array,   required: true },
  visibleIds: { type: Array,   required: true },
  loading:    { type: Boolean, default: false  },
})

const emit = defineEmits(['toggle'])

const copiedId = ref(null)
let copiedTimer = null

async function copyText(id, text) {
  if (!text) return
  try {
    await navigator.clipboard.writeText(text)
    copiedId.value = id
    clearTimeout(copiedTimer)
    copiedTimer = setTimeout(() => { copiedId.value = null }, 2000)
  } catch {
    // Clipboard unavailable (permissions / insecure context) — leave icon as-is
  }
}
</script>
